import {
  FC,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { defaultTheme, MaestroThemeProvider } from '@landr/maestro';
import parse from 'html-react-parser';
import { StickyContext } from 'components/StickyContext';
import { HeaderProps, Header, Footer } from 'layouts';
import { PostFragmentFragment, VideoMetaTag } from 'types';
import { VideoSeoMetaTags } from './SeoVideoMeta';

interface SeoHeadProps {
  seoHead?: string | null;
  videoSeoMetaTags?: VideoMetaTag[];
}

export const SeoHead: FC<SeoHeadProps> = ({ seoHead, videoSeoMetaTags }) => {
  const fullHead = seoHead ? parse(seoHead) : null;

  if (!fullHead) {
    return null;
  }

  return (
    <NextHead>
      {/* // add favicon as seo header doesn't contain it */}
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="/temp-favicon.png"
      />
      {videoSeoMetaTags && <VideoSeoMetaTags metaTags={videoSeoMetaTags} />}
      {fullHead}
    </NextHead>
  );
};

const ReadyContent: FC<{ headerColor: PageWrapperProps['headerColor'] }> = ({
  children,
  headerColor = '#8445D4',
}) => {
  const { isReady } = useRouter();
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const stickyBoundaryRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSticky = () => {
    timeoutRef.current = setTimeout(() => {
      setIsSticky(true);
    }, 500);
  };

  const handleUnSticky = () => {
    setIsSticky(false);
  };

  useEffect(() => {
    if (!isReady) {
      return;
    } else {
      const header = stickyBoundaryRef.current;

      const observer = new IntersectionObserver(
        function (entries) {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) handleSticky();
            if (entry.isIntersecting) handleUnSticky();
          });
        },
        { root: null }
      );

      header && observer.observe(header);

      return () => {
        timeoutRef.current && clearTimeout(timeoutRef.current);
      };
    }
  }, [isReady]);

  return (
    <StickyContext.Provider value={{ isSticky, stickyBoundaryRef }}>
      <Header color={headerColor} />
      {children}
    </StickyContext.Provider>
  );
};

interface TranslationsContextType {
  translations?: PostFragmentFragment['translations'];
}

const TranslationsContext = createContext<TranslationsContextType>({
  translations: undefined,
});

const TranslationsProvider = TranslationsContext.Provider;

export const useTranslationsContext = () => {
  const context = useContext(TranslationsContext);
  return context;
};

interface PageWrapperProps {
  headerColor?: HeaderProps['color'];
  translations?: PostFragmentFragment['translations'];
}

export const PageWrapper: FC<PageWrapperProps> = ({
  headerColor,
  translations,
  children,
}) => {
  return (
    <TranslationsProvider value={{ translations }}>
      <MaestroThemeProvider theme={defaultTheme}>
        <ReadyContent headerColor={headerColor}>{children}</ReadyContent>
        <Footer />
      </MaestroThemeProvider>
    </TranslationsProvider>
  );
};
